/**
 * CampaignGridPage
 *
 * Multi-campaign performance grid — ported from the `Campaign Grid.html`
 * design bundle (Stellair Option D × Sidekick Teal). Backed by:
 *   GET    /api/v2/campaign-grid                campaigns + cached metrics
 *   GET    /api/v2/campaign-grid/:id/rows       ad-group rows for one campaign
 *   POST   /api/v2/campaign-grid/refresh-metrics       trigger metric pulls
 *   GET    /api/v2/campaign-grid/runs?ids=…             batched run polling
 *
 * Per-tab column groups and expandable campaign/ad-group/ad rows. The right
 * detail panel (Fields / Agents / Runs) is gone: clicking a campaign row or
 * its Details button now opens the campaign in the Campaign Launch Workflow
 * (`/campaign/v3/start?campaign=…`), which owns plan review, the Sidekick
 * agents and — since this change — the campaign run history (the sidebar's
 * Runs tab).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/apiConfig';
import { useAccount } from '../contexts/AccountContext';
import { getUserData } from '../utils/userUtils';
import { TacticPill } from './tacticBadge';
import { formatRefreshedAgo, newestMetricsFetchedAt } from '../utils/refreshedLabel';
import { cur, num, numFlex, rate } from '../utils/metricFormat';
import {
  GRID_TIME_PRESETS, resolveTimeWindow, describeTimeWindow,
  campaignInWindow, loadStoredTimeFilter, storeTimeFilter,
} from '../utils/campaignGridTimeFilter';
import './CampaignGridPage.css';


function destinationFormat(dest) {
  const d = String(dest || '').toLowerCase();
  if (d === 'google-ads-search' || d === 'google_search' || d === 'search') return 'Search';
  if (d === 'google-ads-pmax' || d === 'google_pmax' || d === 'pmax') return 'PMax';
  if (d === 'google-ads-demandgen' || d === 'google_demandgen' || d === 'demandgen') return 'DemandGen';
  if (d === 'g-ads') return 'Search';
  if (d === 'facebook' || d === 'fb') return 'Facebook';
  if (d === 'instagram') return 'Instagram';
  // DV360 and TTD are DSPs, not formats — each serves display, video, OTT and
  // audio. Claiming OLV for the whole platform is how a Crimtan account of pure
  // display line items came to render as online video everywhere. The real
  // format now rides on each row (see utils/dv360Format.js on the server), so
  // this platform-level fallback — used ONLY when a campaign has no pulled rows
  // at all — says nothing rather than guessing video.
  if (d === 'dv360' || d === 'youtube-dv360' || d === 'ttd') return null;
  return 'Other';
}

// Tactic badge — colored chip rendered before the tactic label. The map and
// the label→code derivation live in ./tacticBadge.js so the CLW plan grid
// renders identical chips (Campaign Grid.html:1168 is the shared source).
function TacticBadge({ row }) {
  if (!row.tactic) return <span className="cg-dash">—</span>;
  return <TacticPill tactic={row.tactic} tacticId={row.tactic_id} />;
}

const FORMAT_CLASS = {
  Mobile: 'cg-fmt-mob',
  OLV:    'cg-fmt-olv',
  OTT:    'cg-fmt-ott',
  Display: 'cg-fmt-disp',
  Audio:   'cg-fmt-aud',
  Search: 'cg-fmt-srch',
  PMax:   'cg-fmt-pmax',
  DemandGen: 'cg-fmt-dgen',
  Facebook:  'cg-fmt-fb',
  Instagram: 'cg-fmt-ig',
};

// What to call a row's children, by the child's own `kind`. A DV360 insertion
// order's children are line items, not ads.
const CHILD_LABEL = {
  asset: 'asset',
  adgroup: 'line item',
  ad: 'ad',
};

// ─── Column groups (the 5 tabs above the grid) ────────────────────────────
const TAB_DEFS = [
  { id: 'overview', label: 'Overview', cols: [
    { k: 'format',      l: 'Format' },
    { k: 'market',      l: 'Market' },
    { k: 'tactic',      l: 'Tactic', render: 'tactic' },
    { k: 'status',      l: 'Status' },
    // Pacing removed: it was computed as spend / (budget × days_with_data),
    // and because `budget` from the platform setup pull is a DAILY budget for
    // most campaigns the result read as e.g. 1865% against a $30 budget —
    // indistinguishable from broken data for a reviewer. Reinstate only with a
    // real flight-aware plan (elapsed vs total flight budget).
    // 'Spend / Budget' removed for the same reason as Pacing: it rendered an
    // inline spend/budget percentage off the DAILY budget (680% for $203.88
    // against $30.00), and the "/ budget" half labeled a daily figure as though
    // it were the campaign total. Spend still appears in the header totals and
    // the budget detail panel; the Budget tab keeps the raw budget column.
    // Goal and Target dropped: the metric columns are the four delivered
    // figures only (impressions, clicks, conversions, KPI). The impression
    // "goal" and KPI "target" came from the launch plan, not the platform, and
    // showed as '—' or a stale plan number next to live delivery — noise in a
    // reviewed report.
    // Spend (cost) at row level — campaign, ad group and ad. This is the plain
    // delivered cost, NOT the old 'Spend / Budget' combo: no budget half and no
    // attainment percentage, which is what made that column misleading.
    { k: 'spend',       l: 'Spend',       render: 'cur', num: true },
    { k: 'impressions', l: 'Impressions', render: 'num', num: true },
    { k: 'clicks',      l: 'Clicks',      render: 'num', num: true },
    { k: 'conversions', l: 'Conversions', render: 'numFlex', num: true },
    { k: 'ctr',         l: 'KPI', render: 'kpiCombo' },
  ]},
  { id: 'targeting', label: 'Targeting', cols: [
    { k: 'format',   l: 'Format' },
    { k: 'market',   l: 'Market' },
    { k: 'tactic',   l: 'Tactic', render: 'tactic' },
    { k: 'geo',      l: 'Geo filter' },
    { k: 'audience', l: 'Audience', render: 'audience' },
    { k: 'age',      l: 'Age' },
    { k: 'gender',   l: 'Gender' },
  ]},
  { id: 'budget', label: 'Budget', cols: [
    { k: 'format', l: 'Format' },
    { k: 'tactic', l: 'Tactic' },
    { k: 'budget', l: 'Budget', render: 'cur', num: true },
    { k: 'cpm',    l: 'CPM',    render: 'cpm', num: true },
    { k: 'impressions', l: 'Impressions', render: 'num', num: true },
    { k: 'freqCap',  l: 'Freq cap' },
    { k: 'bidFloor', l: 'Bid floor' },
  ]},
  { id: 'creative', label: 'Creative', cols: [
    { k: 'format',   l: 'Format' },
    { k: 'market',   l: 'Market' },
    { k: 'tactic',   l: 'Tactic', render: 'tactic' },
    { k: 'creative', l: 'Creative file', render: 'creative' },
    { k: 'creative_type', l: 'Type' },
    { k: 'sizes',    l: 'Sizes' },
    { k: 'version',  l: 'Version' },
    { k: 'clickURL', l: 'Click URL' },
    { k: 'sitelinks', l: 'Sitelinks', render: 'sitelinks' },
    { k: 'callouts',  l: 'Callouts',  render: 'list' },
  ]},
  { id: 'tracking', label: 'Tracking', cols: [
    { k: 'format',  l: 'Format' },
    { k: 'market',  l: 'Market' },
    { k: 'tactic',  l: 'Tactic' },
    { k: 'kpi_label', l: 'KPI' },
    { k: 'kpi_target', l: 'Target', render: 'kpiTarget' },
    { k: 'convWindow', l: 'Conv. window' },
    { k: 'pixel',   l: 'Pixel / tag' },
    { k: 'tracking_url', l: 'Tracking URL', render: 'url' },
  ]},
];

// ─── Columns that don't apply to a format ─────────────────────────────────
// Google Ads has no such setting for these, so an empty cell there is not
// missing data. A column is hidden when no campaign on screen can use it, and
// reads "n/a" on a row whose format can't (a mixed account still shows it).
const GOOGLE_ADS_FORMATS = ['Search', 'PMax', 'DemandGen'];
const NOT_APPLICABLE = {
  age:        ['Search', 'PMax'],   // PMax has no demographic targeting; Search ad groups here carry none
  gender:     ['Search', 'PMax'],
  freqCap:    GOOGLE_ADS_FORMATS,
  bidFloor:   ['PMax', 'DemandGen'], // smart bidding only; Search keeps its max CPC
  version:    GOOGLE_ADS_FORMATS,
  convWindow: GOOGLE_ADS_FORMATS,    // set per conversion action, account-wide
  pixel:      GOOGLE_ADS_FORMATS,    // the Google tag is account-level; see Tracking URL
};
// The reverse: columns only these formats fill.
const ONLY_FOR = {
  tracking_url: GOOGLE_ADS_FORMATS,
  sitelinks:    GOOGLE_ADS_FORMATS,
  callouts:     GOOGLE_ADS_FORMATS,
};

// List-valued columns (a row's sitelinks / callouts). A campaign row shows its
// rows' list when they all share one — PMax sitelinks live on the campaign, so
// every asset group serves the same set — and sorts by how many there are.
const listKey = (v) => (Array.isArray(v) ? JSON.stringify(v) : null);

/** Does `col` mean anything for a row of format `fmt`? An unknown format keeps
 *  every general column (it may be a DSP that fills them). */
export function columnApplies(col, fmt) {
  if (ONLY_FOR[col.k]) return ONLY_FOR[col.k].includes(fmt);
  return !fmt || !(NOT_APPLICABLE[col.k] || []).includes(fmt);
}

// ─── Helpers ──────────────────────────────────────────────────────────────
// cur / num / numFlex live in utils/metricFormat.js because the CLW's
// "delivered to date" strip renders the same cached figures and has to format
// them identically — see that file's header.
const dash = '—';

// ─── Sorting ──────────────────────────────────────────────────────────────
// Sort key for the name column, which has no entry in TAB_DEFS.
const NAME_SORT_KEY = '__name';
// Columns whose campaign-row value is the campaign's OWN cached metric rather
// than a rollup of its ad groups — see campaignAggCell's fall-through group.
const CAMPAIGN_METRIC_COLS = new Set(['spend', 'budget', 'impressions', 'clicks', 'conversions', 'cpm']);

/** Distinct non-empty values of `key` across a campaign's ad-group rows. Shared
 *  by the rollup cell and the sort value so the two can't disagree about what a
 *  campaign row is worth in a column. */
function uniqAgValues(ags, key) {
  return [...new Set((ags || []).map(r => r[key]).filter(v => v != null && v !== '').map(String))];
}

/**
 * Null out every windowed performance figure on one row. Used when the user
 * switches the time filter: the cached numbers cover the OLD window, so every
 * grid number renders as the '—' placeholder until the new window's pull
 * lands. Budget is deliberately NOT here — it's a campaign setting, not a
 * windowed metric, and the same value would come straight back.
 */
const blankWindowMetrics = (row) => ({
  ...row,
  spend: null, impressions: null, clicks: null, conversions: null,
  cpm: null, ctr: null, kpi_actual: null,
  metrics_fetched_at: null, metrics_window: null,
});

/** A metric coerced for sorting. Anything that renders as '—' (missing, empty,
 *  unparseable) is null, which sorts last in both directions. */
const numOrNull = (v) => {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * What a campaign row is sorted by in a column: the same value the cell shows,
 * reduced to something comparable. Mirrors campaignAggCell case for case —
 * sorting on a different value than the one on screen is indistinguishable from
 * a broken sort.
 */
export function campaignSortValue(campaign, col, ags) {
  switch (col.k) {
    case 'format': {
      const uniq = uniqAgValues(ags, 'format');
      if (uniq.length === 0) return destinationFormat(campaign.destination);
      return uniq.length === 1 ? uniq[0] : `${uniq.length} formats`;
    }
    case 'market':
    case 'tactic': {
      const uniq = uniqAgValues(ags, col.k);
      if (uniq.length === 0) return null;
      return uniq.length === 1 ? uniq[0] : `${uniq.length} ${col.k}s`;
    }
    case 'status':
      return statusLabel(campaign.platform_status || campaign.status) || null;
    // The KPI column renders the actual rate, so it sorts as a number.
    case 'ctr':
      return numOrNull(campaign.kpi_actual ?? campaign.ctr);
    case 'sitelinks':
    case 'callouts': {
      const lens = (ags || []).map(r => (Array.isArray(r[col.k]) ? r[col.k].length : null)).filter(n => n != null);
      return lens.length ? Math.max(...lens) : null;
    }
    default: {
      if (CAMPAIGN_METRIC_COLS.has(col.k)) return numOrNull(campaign[col.k]);
      const uniq = uniqAgValues(ags, col.k);
      if (uniq.length === 0) return null;
      return uniq.length === 1 ? uniq[0] : `${uniq.length} values`;
    }
  }
}

/** Ascending comparison of two non-null sort values. Numbers compare
 *  numerically; text compares naturally so 'Ad group 9' precedes 'Ad group 10'. */
export function compareSortValues(a, b) {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

/** The ad-group rows backing a campaign row: the lazily-fetched full tree once
 *  the campaign has been expanded, else the collapsed embed from the grid GET. */
function adGroupRowsOf(campaign, adgroupsByCampaign) {
  return adgroupsByCampaign[campaign.id]?.adgroups || campaign.adgroups || [];
}

/** The formats a campaign's rows are, else its destination's; `[null]` when
 *  neither says (a DSP with no pulled rows). */
function campaignFormats(campaign, adgroupsByCampaign) {
  const fmts = uniqAgValues(adGroupRowsOf(campaign, adgroupsByCampaign), 'format');
  if (fmts.length) return fmts;
  return [destinationFormat(campaign.destination)];
}

/**
 * A sortable column header. The label itself is the button so the sort is
 * reachable by keyboard, and aria-sort tells a screen reader which way the
 * column is ordered.
 *
 * Declared at module scope, not inside the page component: a component defined
 * during render is a new type on every render, so React would replace the <th>
 * subtree each time and a keyboard user would lose focus after the first Enter
 * — unable to press it again to flip descending → ascending.
 */
function SortableTh({ sortKey, label, className, style, sort, onSort }) {
  const active = sort?.key === sortKey;
  const nextDir = active && sort.dir === 'desc' ? 'ascending' : 'descending';
  return (
    <th
      className={className}
      style={style}
      aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        className="cg-th-sort"
        onClick={() => onSort(sortKey)}
        title={`Sort by ${label} (${nextDir})`}
      >
        {label}
        <span className="cg-sort-ind">{active ? (sort.dir === 'asc' ? '▲' : '▼') : '⇅'}</span>
      </button>
    </th>
  );
}

// PacingSparkline was removed along with the Pacing column — see the note in
// TAB_DEFS. PerfGridPage still has its own copy for its own Pacing column.

/**
 * A campaign / flight status, rendered the same way whatever produced it.
 *
 * Two vocabularies land in this column and they are formatted differently at
 * source: the PLATFORM sends `ENABLED` / `PAUSED`, and our own pipeline sends
 * snake_case — `ready_to_launch`, `awaiting_creatives`. Rendering the raw string
 * put `ready_to_launch` in a row of `ENABLED` and `PAUSED` pills, reading like a
 * database value that had escaped into the UI.
 *
 * So the label is humanised here (separators → spaces) and cased in CSS, and the
 * colour is chosen from one map covering both vocabularies. `enabled` sits with
 * `active` / `live` rather than falling through to the informational blue it used
 * to get: it is the same state under the platform's name, and colouring one green
 * and the other blue said they differed when they do not.
 */
const STATUS_TONES = [
  // Running.
  [['active', 'live', 'enabled', 'running'], 'cg-pill-green'],
  // Prepared or in flight — not yet delivering, nothing wrong.
  [['ready', 'ready_to_launch', 'launching', 'processing'], 'cg-pill-blue'],
  // Halted, or waiting on someone.
  [['paused', 'stopped', 'awaiting_creatives', 'awaiting_download'], 'cg-pill-amber'],
  // Broken or gone.
  [['failed', 'error', 'removed'], 'cg-pill-red'],
  // Inert.
  [['draft', 'archived', 'completed'], 'cg-pill-gray'],
];

export function statusTone(status) {
  const s = String(status || '').toLowerCase().trim();
  const hit = STATUS_TONES.find(([values]) => values.includes(s));
  // Anything unmapped keeps the informational blue rather than being asserted as
  // good or bad — a status we do not recognise is not a status we can judge.
  return hit ? hit[1] : 'cg-pill-blue';
}

export function statusLabel(status) {
  // Casing is CSS's job (.cg-pill-status), so this only has to fix separators.
  return String(status || '').replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
}

function StatusPill({ status }) {
  if (!status) return <span className="cg-dash">—</span>;
  return (
    <span className={`cg-pill cg-pill-status ${statusTone(status)}`} title={String(status)}>
      {statusLabel(status)}
    </span>
  );
}

// DSP QA state from campaign_reconciliation. Campaigns that never entered QA
// (c.qa == null) get no badge at all rather than a misleading "not signed off".
function qaBadge(c) {
  const qa = c?.qa;
  if (!qa) return null;
  if (qa.signedOff) {
    return (
      <span className="cg-pill cg-pill-green" title={`QA signed off ${qa.signedOffAt ? new Date(qa.signedOffAt).toLocaleString() : ''}`}>
        ✓ QA signed off
      </span>
    );
  }
  const open = qa.openCount || 0;
  return (
    <span className="cg-pill cg-pill-amber" title="DSP QA is still open for this campaign">
      ⧗ In QA{open > 0 ? ` · ${open} open` : ''}
    </span>
  );
}

function FormatBadge({ fmt }) {
  if (!fmt) return <span className="cg-dash">—</span>;
  const cls = FORMAT_CLASS[fmt] || 'cg-fmt-other';
  return <span className={`cg-fmt ${cls}`}>{String(fmt).toUpperCase()}</span>;
}

/** Build placeholder ad-group rows for a campaign that hasn't been metrics-pulled yet.
 *  Mirrors the TACTIC_DEFS expansion from the prototype but keyed off destination. */
function buildPlaceholderRows(campaign) {
  const fmt = destinationFormat(campaign.destination);
  const baseBudget = Number(campaign.budget || 0);
  const baseImps = Number(campaign.impressions || 0);
  const tactics = fmt === 'Search' || fmt === 'PMax' || fmt === 'DemandGen'
    ? [{ id: 'kw', label: 'Keyword themes', geo: 'All markets', audience: 'Search intent' }]
    : fmt === 'Facebook' || fmt === 'Instagram'
      ? [
          { id: 'aud', label: 'Audience set',   geo: 'US (all)', audience: 'Lookalike + interest' },
          { id: 'ret', label: 'Retargeting',    geo: 'US (all)', audience: 'Site visitors 30d' },
        ]
      : [
          { id: 'beh', label: 'Behavioral',    geo: 'Zip code', audience: '3rd-party segments' },
          { id: 'ret', label: 'Retargeting',   geo: 'Zip code', audience: 'Retargeting list' },
        ];
  return tactics.map((t, i) => ({
    external_id: `placeholder-${campaign.id}-${t.id}`,
    kind: 'adgroup',
    parent_external_id: null,
    name: `${campaign.name} — ${t.label}`,
    format: fmt,
    market: campaign.market || 'All markets',
    tactic: t.label,
    status: campaign.status || 'draft',
    budget: baseBudget ? +(baseBudget / tactics.length).toFixed(2) : null,
    spend: null,
    impressions: baseImps ? Math.round(baseImps / tactics.length) : null,
    clicks: null, ctr: null, conversions: null,
    geo: t.geo, audience: t.audience,
    age: 'Ages 25–54', gender: 'All', freqCap: 'No cap', bidFloor: '—',
    cpm: null,
    creative: '—', creative_type: '—', sizes: '—', version: '—', clickURL: '—',
    kpi_label: '—', kpi_target: null, convWindow: '—', pixel: '—',
    placeholder: true,
    ads: [],
  }));
}

/** Only http(s) values become links or image sources — these strings come from
 *  ad platforms, and a `javascript:` href would run on click. */
const httpUrl = (v) => (typeof v === 'string' && /^https?:\/\//i.test(v) ? v : null);

/** Thumbnails for a row's images / YouTube videos, each opening the original. */
function MediaThumbs({ media, total }) {
  const items = (media || []).map((m, i) => {
    if (m.type === 'video' && m.youtube_id) {
      const id = encodeURIComponent(m.youtube_id);
      return (
        <a key={`v${i}`} className="cg-thumb cg-thumb-video" href={`https://www.youtube.com/watch?v=${id}`}
           target="_blank" rel="noopener noreferrer" title={m.title || 'YouTube video'}
           onClick={e => e.stopPropagation()}>
          <img src={`https://i.ytimg.com/vi/${id}/default.jpg`} alt={m.title || 'YouTube video'} loading="lazy" />
          <span className="cg-thumb-play" aria-hidden="true">▶</span>
        </a>
      );
    }
    const url = httpUrl(m.url);
    if (!url) return null;
    return (
      <a key={`i${i}`} className="cg-thumb" href={url} target="_blank" rel="noopener noreferrer"
         title={m.field || 'Image'} onClick={e => e.stopPropagation()}>
        <img src={url} alt={m.field || 'Image'} loading="lazy" />
      </a>
    );
  }).filter(Boolean);
  if (!items.length) return null;
  const more = (total || 0) - items.length;
  return (
    <span className="cg-media">
      {items}
      {more > 0 && <span className="cg-thumb-more">+{more}</span>}
    </span>
  );
}

/** Whittle a numeric or string value to a renderable cell. */
function renderCell(col, row) {
  if (!columnApplies(col, row.format)) return <span className="cg-dash" title="Not applicable to this campaign type">n/a</span>;
  const v = row[col.k];
  switch (col.render) {
    case 'tactic':    return <TacticBadge row={row} />;
    case 'cur':       return <span className="cg-num">{cur(v, row.currency_code)}</span>;
    case 'num':       return <span className="cg-num">{num(v)}</span>;
    case 'numFlex':   return <span className="cg-num">{numFlex(v)}</span>;
    case 'cpm':       return <span className="cg-num">{v == null ? '—' : cur(v, row.currency_code)}</span>;
    // 'spendCombo' removed with the Spend / Budget column (see TAB_DEFS).
    // 'impsCombo' removed with the impression goal — impressions now render
    // through the plain 'num' case.
    case 'creative': {
      const thumbs = <MediaThumbs media={row.media} total={row.media_total} />;
      // An image / video asset's `creative` is its own URL — the thumbnail
      // already says that, so the text would only repeat it.
      const text = row.media && httpUrl(v) ? null : v;
      if (!row.media && (v == null || v === '')) return <span className="cg-dash">{dash}</span>;
      return (
        <span className="cg-creative-cell">
          {thumbs}
          {text != null && text !== '' && <span className="cg-creative-text">{String(text)}</span>}
        </span>
      );
    }
    case 'audience': {
      // A Search ad group with no audience lists targets by keyword.
      const text = v || row.keywords;
      return text ? String(text) : <span className="cg-dash">{dash}</span>;
    }
    case 'sitelinks': {
      if (!Array.isArray(v)) return <span className="cg-dash">{dash}</span>;
      if (!v.length) return <span className="cg-dash">None</span>;
      const more = (row.sitelinks_total || v.length) - v.length;
      return (
        <span className="cg-chips">
          {v.map((s, i) => {
            const url = httpUrl(s.url);
            const title = [s.description, s.url].filter(Boolean).join('\n') || undefined;
            return url
              ? <a key={i} className="cg-chip" href={url} target="_blank" rel="noopener noreferrer"
                   title={title} onClick={e => e.stopPropagation()}>{s.text}</a>
              : <span key={i} className="cg-chip" title={title}>{s.text}</span>;
          })}
          {more > 0 && <span className="cg-thumb-more">+{more}</span>}
        </span>
      );
    }
    case 'list': {
      if (!Array.isArray(v)) return <span className="cg-dash">{dash}</span>;
      if (!v.length) return <span className="cg-dash">None</span>;
      return <span title={v.join(' · ')}>{v.join(' · ')}</span>;
    }
    case 'url': {
      if (v == null || v === '') return <span className="cg-dash">{dash}</span>;
      return <span className="cg-url" title={String(v)}>{String(v)}</span>;
    }
    case 'kpiTarget': {
      if (v == null || v === '') return <span className="cg-dash">{dash}</span>;
      // tROAS is a ratio, tCPA money — the KPI label says which.
      if (/roas/i.test(row.kpi_label || '')) return <span className="cg-num">{Number(v).toFixed(1)}x</span>;
      return <span className="cg-num">{cur(v, row.currency_code)}</span>;
    }
    case 'kpiCombo': {
      // Actual only. The target (and the met/miss pill derived from it) came
      // from the launch plan rather than the platform.
      const actual = row.kpi_actual ?? row.ctr;
      const label = row.kpi_label || 'CTR';
      return (
        <span>
          <strong>{label}</strong>: {rate(actual)}
        </span>
      );
    }
    default: {
      if (v == null || v === '') return <span className="cg-dash">{dash}</span>;
      if (col.k === 'format') return <FormatBadge fmt={v} />;
      if (col.k === 'status') return <StatusPill status={v} />;
      return String(v);
    }
  }
}

// ─── Main page ────────────────────────────────────────────────────────────
function CampaignGridPage() {
  const navigate = useNavigate();
  const { getAccountIdToUse } = useAccount();
  const accountId = getAccountIdToUse();
  // The extractUser middleware on /api/v2/campaign-grid/* requires userId
  // alongside accountId on every call — fetch once and reuse below.
  const [user] = useState(() => getUserData());
  const userId = user?.userId || user?.id;
  const [campaigns, setCampaigns] = useState([]);
  const [adgroupsByCampaign, setAdgroupsByCampaign] = useState({});
  const [expandedIds, setExpandedIds] = useState(new Set());
  // Independently expanded ad groups, keyed `${campaignId}:${adGroupExternalId}`,
  // so each ad group can expand/collapse to its ads (Campaign → AdGroup → Ad).
  const [expandedAdGroups, setExpandedAdGroups] = useState(new Set());
  const toggleAdGroup = (key) => setExpandedAdGroups(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const [activeTab, setActiveTab] = useState('overview');
  // Column sort: null = the server's default order (most recently updated
  // first). A header click sorts descending, clicking the same header again
  // flips to ascending.
  const [sort, setSort] = useState(null);
  const [search, setSearch] = useState('');
  const [qaSignedOffOnly, setQaSignedOffOnly] = useState(false);
  // Time filter — WHICH CAMPAIGNS ran in the window (a flight-overlap test).
  // The server applies it so LIMIT paginates the filtered set; the numbers in
  // each row stay lifetime totals (there is no per-day metric store yet), and
  // the toolbar says so rather than implying windowed figures.
  // Restored from the last visit (per account): the picker used to reset to
  // "All time" on every load while the cached dataset kept whatever window the
  // last successful pull ran with — the page silently showed windowed numbers
  // under an unwindowed filter.
  const [timeFilter, setTimeFilter] = useState(() => loadStoredTimeFilter(accountId));
  useEffect(() => { storeTimeFilter(accountId, timeFilter); }, [accountId, timeFilter]);
  // Account switches re-mount this page (NewLayout keys the content subtree),
  // so the lazy initializer re-runs with the new account's stored filter.
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  const [runs, setRuns] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const refreshPollTimer = useRef(null); // single batched poll handle for manual refresh
  // True from the moment the user picks a new time filter until that window's
  // pull completes (or fails). While set, EVERY grid number — row cells and
  // the header strip — renders as a placeholder: the timeWindow effect below
  // refetches the grid as soon as the filter changes, and without this flag
  // that refetch resurrects the previous window's cached numbers mid-pull.
  const [windowPending, setWindowPending] = useState(false);
  const windowPendingRef = useRef(false);
  const setWindowPendingBoth = (v) => { windowPendingRef.current = v; setWindowPending(v); };

  // The active window, resolved from the preset/custom pair. Recomputed on
  // render (cheap, pure) and mirrored into a ref so the async callers that
  // reload after a run completes send the SAME filter the user is looking at.
  const timeWindow = useMemo(() => resolveTimeWindow(timeFilter), [timeFilter]);
  const timeWindowRef = useRef(timeWindow);
  useEffect(() => { timeWindowRef.current = timeWindow; }, [timeWindow]);

  // Changing the window is a data operation, not a client-side filter: the
  // cached numbers cover the OLD window, so they blank immediately (a stale
  // total posing as the new window is worse than an empty cell) and the pull
  // for the new window starts without a second click. The picker choice is
  // persisted per account by the storeTimeFilter effect above.
  const changeTimeWindow = (preset) => {
    const next = { preset, startDate: '', endDate: '' };
    setTimeFilter(next);
    // The ref updates via effect AFTER this render — set it now so the
    // auto-triggered pull below asks for the window just chosen.
    timeWindowRef.current = resolveTimeWindow(next);
    setWindowPendingBoth(true);
    setCampaigns(prev => prev.map(blankWindowMetrics));
    setAdgroupsByCampaign({});
    // NOT dispatched here any more. The server now sums the range out of the
    // per-day rows, so the window may already be answerable with no pull at
    // all — and firing one unconditionally is what made every window change
    // cost a reporting-API call per advertiser (an asynchronous Bid Manager
    // report each, on DV360). loadCampaigns decides once it can see how much
    // of the page the daily rows actually cover. See maybePullForWindow.
    loadCampaigns();
  };

  // Custom range — typing dates only REFETCHES ROWS (a cheap server-side
  // flight filter via the timeWindow effect below). The metrics pull is a
  // slow reporting-API call, so it never fires off the back of typing: it
  // waits for the explicit Fetch button (fetchCustomWindow) once the range
  // is complete. Selecting "Custom range…" just reveals the two date inputs
  // (the current view stays put); a half-filled range stays open on the
  // missing side (resolveTimeWindow's one-sided window).
  const changeCustomWindow = (patch) => {
    const next = { ...timeFilter, ...patch };
    setTimeFilter(next);
    timeWindowRef.current = resolveTimeWindow(next);
  };

  // The custom range's Fetch button — the same data operation as a preset
  // pick (blank the old window's numbers, dispatch the pull for exactly
  // this range), but on the user's say-so: the reporting-API pull takes a
  // while, so it must not fire the instant the range happens to be complete.
  // The button stays disabled from the click until the pull lands
  // (refreshing / windowPending cover the whole in-flight span).
  const fetchCustomWindow = () => {
    const w = resolveTimeWindow(timeFilter);
    if (!w?.startDate || !w?.endDate) return;
    timeWindowRef.current = w;
    setWindowPendingBoth(true);
    setCampaigns(prev => prev.map(blankWindowMetrics));
    setAdgroupsByCampaign({});
    refreshAllRef.current?.();
  };

  // ─── Data loading ─────────────────────────────────────────────────────
  const loadCampaigns = async () => {
    if (!accountId) return;
    setLoading(true);
    setError(null);
    try {
      const w = timeWindowRef.current;
      const r = await api.get(`/api/v2/campaign-grid`, {
        params: {
          accountId,
          userId,
          // Omitted entirely when unfiltered, so the request is byte-identical
          // to the pre-filter one for the default view.
          ...(w?.startDate ? { startDate: w.startDate } : {}),
          ...(w?.endDate ? { endDate: w.endDate } : {}),
        },
      });
      const rows = r.data.campaigns || [];
      // WHICH ROWS THE SERVER COULD ANSWER FROM THE PER-DAY TABLE. Those carry
      // real numbers for exactly this window and must NOT be blanked — blanking
      // them is what made a window switch look empty when the answer was
      // already in hand. Rows it could not window keep being masked while a
      // pull is in flight, because their stored total is for some OTHER range
      // and showing it under this filter is the mixed-vintage bug.
      const windowedRows = rows.filter((c) => c.metrics_source === 'daily');
      setCampaigns(windowPendingRef.current
        ? rows.map((c) => (c.metrics_source === 'daily' ? c : blankWindowMetrics(c)))
        : rows);
      maybePullForWindow(rows.length, windowedRows.length);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  const loadAdGroups = async (campaignId, attempt = 0) => {
    try {
      const r = await api.get(`/api/v2/campaign-grid/${campaignId}/rows`, { params: { accountId, userId } });
      const fetched = r.data.adgroups || [];
      // Same mid-switch rule as the campaign rows: an expand during a window
      // pull must not show ad-group numbers from the previous window.
      const adgroups = windowPendingRef.current ? fetched.map(blankWindowMetrics) : fetched;
      setAdgroupsByCampaign(prev => ({
        ...prev,
        [campaignId]: adgroups.length > 0
          ? { adgroups }
          : { adgroups: buildPlaceholderRows(campaigns.find(c => c.id === campaignId) || {}), placeholder: true },
      }));
      // A live grid-pull is in flight — re-poll (handler → GCS → minute-ly
      // finalize sweep) so live rows replace any stale launch-response fallback
      // without a manual reload. Cap at ~2 min.
      if (r.data.pulling && attempt < 24) {
        setTimeout(() => loadAdGroups(campaignId, attempt + 1), 5000);
      }
    } catch (e) {
      const c = campaigns.find(c => c.id === campaignId);
      if (c) setAdgroupsByCampaign(prev => ({
        ...prev,
        [campaignId]: { adgroups: buildPlaceholderRows(c), placeholder: true },
      }));
    }
  };

  /** Per-column aggregate value for the campaign row. Mirrors the design's
   *  getColAgg shape — value comes from rolled-up ad-groups when expanded,
   *  otherwise from the cached campaign-level metrics row. */
  const campaignAggCell = (campaign, col, adgroups) => {
    const ags = adgroups || [];
    switch (col.k) {
      case 'format': {
        const uniq = uniqAgValues(ags, 'format');
        if (uniq.length === 0) return <FormatBadge fmt={destinationFormat(campaign.destination)} />;
        if (uniq.length === 1) return <FormatBadge fmt={uniq[0]} />;
        return <span><strong>{uniq.length}</strong> formats</span>;
      }
      case 'market': {
        const uniq = uniqAgValues(ags, 'market');
        if (uniq.length === 0) return <span className="cg-dash">—</span>;
        if (uniq.length === 1) return uniq[0];
        return <span><strong>{uniq.length}</strong> markets</span>;
      }
      case 'tactic': {
        const uniq = uniqAgValues(ags, 'tactic');
        if (uniq.length === 0) return <span className="cg-dash">—</span>;
        if (uniq.length === 1) return uniq[0];
        return <span><strong>{uniq.length}</strong> tactics</span>;
      }
      case 'status': {
        // Prefer the campaign's OWN platform delivery status (ENABLED / PAUSED)
        // from the grid pull over the internal lifecycle status ('ready'). We
        // do NOT roll up ad-group statuses: an ENABLED campaign whose older ad
        // groups are paused would otherwise read as PAUSED.
        if (campaign.platform_status) return <StatusPill status={campaign.platform_status} />;
        return <StatusPill status={campaign.status} />;
      }
      case 'spend':
      case 'budget':
      case 'impressions':
      case 'clicks':
      case 'conversions':
      case 'ctr':
      case 'cpm':
        // Fall through to renderCell with the campaign object as the row.
        return renderCell(col, campaign);
      case 'sitelinks':
      case 'callouts': {
        const lists = ags.filter(r => Array.isArray(r[col.k]));
        const uniq = [...new Set(lists.map(r => listKey(r[col.k])))];
        if (uniq.length === 0) return <span className="cg-dash">—</span>;
        if (uniq.length === 1) return renderCell(col, lists[0]);
        return <span><strong>{uniq.length}</strong> values</span>;
      }
      case 'audience': {
        // Same fallback as the row cell: keyword-targeted Search groups.
        const uniq = [...new Set(ags.map(r => r.audience || r.keywords).filter(Boolean).map(String))];
        if (uniq.length === 0) return <span className="cg-dash">—</span>;
        if (uniq.length === 1) return <span>{uniq[0]}</span>;
        return <span><strong>{uniq.length}</strong> values</span>;
      }
      default: {
        // Targeting / Creative / Tracking columns (audience, creative, sizes,
        // kpi_label, geo, bidFloor, …): roll up from the expanded ad-group rows
        // — one shared value, else a "N values" summary.
        const uniq = uniqAgValues(ags, col.k);
        if (uniq.length === 0) return <span className="cg-dash">—</span>;
        if (uniq.length === 1) return <span>{uniq[0]}</span>;
        return <span><strong>{uniq.length}</strong> values</span>;
      }
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  // Refetch on account OR window change — the filter is applied server-side,
  // so a new window is a new query, not a client-side re-slice.
  useEffect(() => { loadCampaigns(); }, [accountId, timeWindow?.startDate, timeWindow?.endDate]);

  // ─── Filtered campaigns ──────────────────────────────────────────────
  // `qa` comes from GET /campaign-grid (campaign_reconciliation). It's null for
  // campaigns that never entered DSP QA — those are unaffected by the toggle
  // unless it's on, which narrows to signed-off only.
  const filteredCampaigns = useMemo(() => {
    const q = search.trim().toLowerCase();
    return campaigns.filter(c => {
      // The window is enforced SERVER-side (so LIMIT paginates the filtered
      // set); this re-applies it as a belt-and-braces check for the one case
      // that bites in this repo — a frontend deployed ahead of the backend,
      // where the old route ignores the params and would hand back unfiltered
      // rows while the toolbar says a window is on. Same predicate, same
      // semantics (utils/campaignGridTimeFilter.js), so on data the server
      // already filtered it can only ever agree.
      if (!campaignInWindow(c, timeWindow)) return false;
      if (qaSignedOffOnly && !c.qa?.signedOff) return false;
      if (!q) return true;
      return c.name?.toLowerCase().includes(q)
        || c.destination?.toLowerCase().includes(q)
        || c.folder_name?.toLowerCase().includes(q);
    });
  }, [campaigns, search, qaSignedOffOnly, timeWindow]);

  // How many visible rows carry metrics refreshed for EXACTLY the active
  // window (the metrics_window stamp finalizeRun writes). Drives the toolbar
  // label: 'windowed metrics' vs 'refresh in flight'.
  const windowedRowCount = useMemo(() => {
    if (!timeWindow) return 0;
    return filteredCampaigns.filter(c => {
      const mw = c.metrics_window;
      if (!mw || typeof mw !== 'object') return false;
      return (mw.start_date || null) === (timeWindow.startDate || null)
        && (mw.end_date || null) === (timeWindow.endDate || null);
    }).length;
  }, [filteredCampaigns, timeWindow]);

  // ─── Sorted campaigns ─────────────────────────────────────────────────
  // Active tab's column set. Derived here rather than at render time because
  // the sort memo below needs the column definition to compare a cell.
  const visibleColsOf = (tab) => tab.cols.filter(col => filteredCampaigns.some(
    c => campaignFormats(c, adgroupsByCampaign).some(fmt => columnApplies(col, fmt))
  ));
  const cols = visibleColsOf(TAB_DEFS.find(t => t.id === activeTab));

  // Only campaign rows are re-ordered. An expanded campaign's ad groups keep
  // their platform order underneath their parent, so a sort never reshuffles
  // the tree a reviewer is reading.
  const sortedCampaigns = useMemo(() => {
    const col = sort && sort.key !== NAME_SORT_KEY ? cols.find(c => c.k === sort.key) : null;
    if (!sort || (sort.key !== NAME_SORT_KEY && !col)) return filteredCampaigns;
    const valueOf = (c) => (col
      ? campaignSortValue(c, col, adGroupRowsOf(c, adgroupsByCampaign))
      : (c.name || null));
    const factor = sort.dir === 'asc' ? 1 : -1;
    // Array.prototype.sort is stable, so campaigns that tie keep the server's
    // order. Missing values sort last in BOTH directions — a screenful of '—'
    // at the top of an ascending sort is indistinguishable from broken data.
    return [...filteredCampaigns].sort((a, b) => {
      const av = valueOf(a);
      const bv = valueOf(b);
      if (av === null || bv === null) return av === bv ? 0 : (av === null ? 1 : -1);
      return compareSortValues(av, bv) * factor;
    });
  }, [filteredCampaigns, sort, cols, adgroupsByCampaign]);

  /** Header click: a new column starts descending, the same column flips. */
  const toggleSort = (key) => setSort(prev => (
    prev?.key === key ? { key, dir: prev.dir === 'desc' ? 'asc' : 'desc' } : { key, dir: 'desc' }
  ));

  /** Switching tabs drops a sort whose column isn't in the new tab, rather than
   *  leaving an invisible sort applied to the rows. */
  const selectTab = (tab) => {
    setActiveTab(tab.id);
    setSort(prev => (
      !prev || prev.key === NAME_SORT_KEY || tab.cols.some(c => c.k === prev.key) ? prev : null
    ));
  };

  // ─── Aggregate stats for the header strip ─────────────────────────────
  const aggregate = useMemo(() => {
    let budget = 0, spend = 0, imps = 0, n = 0, clicks = 0, conversions = 0;
    const currencies = new Set();
    for (const c of filteredCampaigns) {
      n++;
      budget += Number(c.budget || 0);
      spend  += Number(c.spend || 0);
      imps   += Number(c.impressions || 0);
      clicks += Number(c.clicks || 0);
      // conversions arrives as TEXT from the metrics JSON; Number('') is 0 and
      // Number(null) is 0, so guard against a non-numeric value poisoning the
      // whole total with NaN.
      const conv = Number(c.conversions);
      if (Number.isFinite(conv)) conversions += conv;
      // Only campaigns that actually put a number into one of the money totals
      // can corrupt it. A campaign with no spend and no budget contributes
      // nothing, so its currency is irrelevant — counting it would blank both
      // figures over a row that changed neither. Same rule as the Account read
      // scorecard, so the two surfaces agree about what "mixed" means.
      if (c.currency_code && (Number(c.spend || 0) !== 0 || Number(c.budget || 0) !== 0)) {
        currencies.add(c.currency_code);
      }
    }
    // MONEY IS NOT SUMMED ACROSS CURRENCIES. Spend and budget are stored in each
    // advertiser's own currency and nothing here converts, so adding them
    // produces a figure that is not a quantity of anything.
    //
    // This code already SPOTTED the problem — `currencies.size === 1 ? ... :
    // null` — and its own comment said "the sum is not meaningful in any single
    // currency anyway". It then rendered the number regardless, and because
    // cur(v, null) falls back to a plain '$', the response to detecting mixed
    // currency was to drop the honest label and keep the meaningless total
    // formatted to look like dollars. Withholding it is the point; labelling it
    // was never the hard part.
    //
    // The counts are untouched. Impressions, clicks and conversions are
    // currency-free and stay exactly as true as they were.
    const mixedCurrency = currencies.size > 1;
    const currencyList = [...currencies].sort();
    const money = (v) => (mixedCurrency ? null : v);
    if (windowPending) {
      // Masked rows sum to 0, and a “$0.00” total mid-switch reads as a real
      // number. The strip shows placeholders until the window's pull lands.
      return { count: n, budget: money(budget), spend: null, imps: null, clicks: null, conversions: null,
               currency: mixedCurrency ? null : [...currencies][0] || null,
               mixedCurrency, currencies: currencyList };
    }
    return {
      count: n,
      budget: money(budget),
      spend: money(spend),
      imps,
      clicks,
      conversions,
      // Only meaningful when every visible campaign shares one currency; when
      // they do not, the totals above are withheld rather than mislabelled.
      currency: mixedCurrency ? null : [...currencies][0] || null,
      mixedCurrency,
      currencies: currencyList,
    };
  }, [filteredCampaigns, windowPending]);

  // When the grid last successfully pulled anything. Shared with PerfGridPage so
  // the two pages can't report different staleness off the same column — they
  // had already drifted, the grid reducing with the oldest timestamp while perf
  // used the newest. See utils/refreshedLabel.js for why newest is correct.
  const lastRefreshedAt = useMemo(
    () => newestMetricsFetchedAt(filteredCampaigns),
    [filteredCampaigns],
  );

  const lastRefreshedAtLabel = useMemo(
    () => formatRefreshedAgo(lastRefreshedAt) ?? 'Metrics not yet refreshed',
    [lastRefreshedAt],
  );

  // ─── Row interactions ────────────────────────────────────────────────
  // Campaign rows open CLW — the surface that owns plan review, the Sidekick
  // agents, optimization and the run history. Expansion moved onto the
  // chevron only, so a row click and the Details button mean the same thing
  // instead of opening a second, divergent panel on this page.
  // `from=grid` so CLW's review header sends the user back HERE rather than to
  // the campaigns list — this grid, not /campaigns, is where they came from.
  // (`from=launch` is the only value that also switches the rendered route; see
  // App.js CampaignStartSwitch.)
  const openCampaignInClw = (c) => {
    if (!c?.id) return;
    navigate(`/campaign/v3/start?campaign=${encodeURIComponent(c.id)}&from=grid`);
  };
  const toggleExpand = (c) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(c.id)) next.delete(c.id);
      // GET /campaign-grid embeds ad groups + asset groups + ads for the
      // collapsed aggregate, but NOT leaf assets (they explode the payload for
      // PMax accounts). On expand, lazy-fetch the full per-campaign tree
      // (assets included) so PMax asset groups can expand to their assets.
      else { next.add(c.id); if (!adgroupsByCampaign[c.id]) loadAdGroups(c.id); }
      return next;
    });
  };

  useEffect(() => () => {
    if (refreshPollTimer.current) clearInterval(refreshPollTimer.current);
  }, []);

  // Poll ALL refresh runs in ONE batched request every 4s; reload the grid
  // exactly once when they all finish (or after a 3-min safety timeout).
  const refreshAllRef = useRef(null);

  // How long to keep polling before giving up. Google Ads answers from a live
  // API and lands well inside three minutes. DV360's delivery half is an
  // ASYNCHRONOUS Bid Manager report — the fetcher polls it up to 100 times with
  // up to five minutes between attempts — so a three-minute ceiling gave up
  // while the pull was still perfectly healthy, then said "Refresh complete"
  // over an empty grid.
  const DEFAULT_POLL_CEILING_MS = 180000;
  const SLOW_PLATFORM_POLL_CEILING_MS = 900000;   // DV360 / Bid Manager
  const SLOW_PLATFORMS = new Set(['dv360']);

  const pollRunsBatch = (runIds, { ceilingMs = DEFAULT_POLL_CEILING_MS } = {}) => {
    if (refreshPollTimer.current) clearInterval(refreshPollTimer.current);
    const remaining = new Set(runIds);
    const startedAt = Date.now();
    const finish = ({ timedOut = false } = {}) => {
      clearInterval(refreshPollTimer.current);
      refreshPollTimer.current = null;
      setRefreshing(false);
      setWindowPendingBoth(false); // BEFORE the reload, so fresh numbers render
      setAdgroupsByCampaign({}); // expanded rows re-fetch fresh structure/metrics
      loadCampaigns();           // single grid reload
      // Don't claim completion over runs that are still going. The pulls keep
      // running server-side and the grid finalizes them on its next load, so
      // the honest message is "still running", not "complete".
      setToast(timedOut
        ? { msg: `Still pulling ${remaining.size} run(s) — reload in a few minutes to see them` }
        : { msg: 'Refresh complete' });
    };
    refreshPollTimer.current = setInterval(async () => {
      try {
        const r = await api.get(`/api/v2/campaign-grid/runs`, {
          params: { accountId, userId, ids: runIds.join(',') },
        });
        const rows = r.data?.runs || [];
        if (rows.length) setRuns(prev => prev.map(x => rows.find(rr => rr.id === x.id) || x));
        for (const rr of rows) {
          if (rr.status === 'success' || rr.status === 'error') remaining.delete(rr.id);
        }
        if (remaining.size === 0) finish();
        else if (Date.now() - startedAt > ceilingMs) finish({ timedOut: true });
      } catch (_) {
        // transient; keep polling until the safety timeout
        if (Date.now() - startedAt > ceilingMs) finish({ timedOut: true });
      }
    }, 4000);
  };

  /**
   * Dispatch a pull only for what the per-day rows could not answer.
   *
   * Full coverage means the window is already correct on screen: clear the
   * pending mask and stop, with no reporting-API call. Anything less still
   * needs a pull to FILL that coverage — the days for the uncovered rows do
   * not exist yet, and only a pull can create them.
   *
   * Called from loadCampaigns because that is the first moment coverage is
   * knowable; deciding in changeTimeWindow would have to guess.
   */
  const maybePullForWindow = (total, windowed) => {
    if (!windowPendingRef.current) return;
    if (total > 0 && windowed === total) {
      setWindowPendingBoth(false);
      setToast({ msg: 'Window applied from stored daily data' });
      return;
    }
    refreshAllRef.current?.();
  };

  // ─── Manual refresh: pull latest campaign setup + perf for all on the page ─
  const refreshAll = async () => {
    if (!accountId || refreshing) return;
    setRefreshing(true);
    try {
      const ids = campaigns.map(c => c.id);
      // Refresh pulls the reporting APIs for the ACTIVE window, so the numbers
      // that come back cover what the filter shows.
      const w = timeWindowRef.current;
      const r = await api.post(`/api/v2/campaign-grid/refresh-metrics`, {
        accountId,
        userId,
        campaignIds: ids,
        ...(w?.startDate ? { startDate: w.startDate } : {}),
        ...(w?.endDate ? { endDate: w.endDate } : {}),
        // An explicit "All time" must reach the server as a choice, not as an
        // absence — an absent window now defaults to the trailing 30 days.
        ...(w ? {} : { datePreset: 'all_time' }),
      });
      const dispatched = r.data?.dispatched || [];
      if (dispatched.length === 0) {
        setRefreshing(false);
        setWindowPendingBoth(false);
        setToast({ msg: 'No campaigns eligible to refresh' });
        return;
      }
      const newRuns = dispatched.map(d => ({
        id: d.runId, campaign_id: d.campaignId, agent_type: d.agentType, status: 'running',
      }));
      setRuns(prev => [...newRuns, ...prev]);
      setToast({ msg: `Refreshing ${dispatched.length} pull${dispatched.length === 1 ? '' : 's'}…` });
      // One batched poll loop instead of one request per run.
      const slow = dispatched.some(d => SLOW_PLATFORMS.has(d.platform));
      pollRunsBatch(dispatched.map(d => d.runId), {
        ceilingMs: slow ? SLOW_PLATFORM_POLL_CEILING_MS : DEFAULT_POLL_CEILING_MS,
      });
    } catch (e) {
      setRefreshing(false);
      // The pull never started — stop masking so the toast + cached rows are
      // at least honest about what's on screen.
      setWindowPendingBoth(false);
      setToast({ msg: e.response?.data?.error || e.message, err: true });
    }
  };

  refreshAllRef.current = refreshAll;

  // Metrics + setup are pulled ON DEMAND only: picking a window blanks the
  // grid and dispatches the pull for exactly that range (changeTimeWindow →
  // refreshAll → POST /refresh-metrics). The daily cron pull is disabled —
  // it re-pulled with its own default window and overwrote whatever window
  // the user had chosen. The freshness indicator below shows the oldest
  // cached row across visible campaigns.

  // ─── Auto-clear toast ─────────────────────────────────────────────────
  useEffect(() => {
    if (!toast) return;
    const h = setTimeout(() => setToast(null), 2500);
    return () => clearTimeout(h);
  }, [toast]);

  // ─── Render ──────────────────────────────────────────────────────────
  const colSpan = cols.length + 2; // expand-chev + name + data cols

  // A blank money total with no explanation reads as a bug or as zero delivery.
  // The strip says which currencies are in play, so the reason is on the page
  // rather than only in a tooltip.
  const mixedCurrencyNote = aggregate.mixedCurrency
    ? `Campaigns on this account report in ${aggregate.currencies.join(', ')}. `
      + 'Amounts are stored in each advertiser\'s own currency and are never converted, '
      + 'so they cannot be added into one total.'
    : undefined;
  const mixedCurrencySuffix = aggregate.mixedCurrency
    ? <span className="cg-ch-stat-note"> · mixed currencies</span>
    : null;

  return (
    <div className="cg-root">
      <div className="cg-topbar">
        <div className="cg-bc">
          {/* The crumb and the title below it both name this page the way the
              sidebar does — "Campaign Grid". It was "Performance Grid" here and
              "Campaign Performance Grid" in the title, so one screen answered to
              three names. */}
          <button type="button" className="cg-bc-link" onClick={() => navigate('/campaigns')}>Campaigns</button> / <strong>Campaign Grid</strong>
        </div>
        <span className="cg-badge">BETA</span>
        <div className="cg-tb-right">
          <span style={{ fontSize: 11, color: 'var(--cg-text-3)' }} title={lastRefreshedAt ? new Date(lastRefreshedAt).toLocaleString() : 'No metrics cached yet'}>
            {lastRefreshedAtLabel}
          </span>
          {refreshing && <span style={{ fontSize: 11, color: 'var(--cg-text-3)' }}>Refreshing…</span>}
        </div>
      </div>

      <div className="cg-camp-header">
        <div>
          <div className="cg-ch-name">Campaign Grid</div>
          {/* The account was shown as a truncated UUID ("Account e000ec04"),
              which is meaningless to a reader and looked like leaked debug
              output in a reviewed report. The account is already established by
              the surrounding page context. */}
          <div className="cg-ch-sub">{aggregate.count} {aggregate.count === 1 ? 'campaign' : 'campaigns'}</div>
        </div>
        <div className="cg-ch-stats">
          <div className="cg-ch-stat" title={mixedCurrencyNote}><div className="cg-ch-stat-v">{cur(aggregate.budget, aggregate.currency)}</div><div className="cg-ch-stat-l">Total daily budget{mixedCurrencySuffix}</div></div>
          <div className="cg-ch-stat-sep" />
          <div className="cg-ch-stat" title={mixedCurrencyNote}><div className="cg-ch-stat-v">{cur(aggregate.spend, aggregate.currency)}</div><div className="cg-ch-stat-l">Spend{mixedCurrencySuffix}</div></div>
          <div className="cg-ch-stat-sep" />
          <div className="cg-ch-stat"><div className="cg-ch-stat-v">{num(aggregate.imps)}</div><div className="cg-ch-stat-l">Impressions</div></div>
          <div className="cg-ch-stat-sep" />
          <div className="cg-ch-stat"><div className="cg-ch-stat-v">{num(aggregate.clicks)}</div><div className="cg-ch-stat-l">Clicks</div></div>
          <div className="cg-ch-stat-sep" />
          <div className="cg-ch-stat"><div className="cg-ch-stat-v">{numFlex(aggregate.conversions)}</div><div className="cg-ch-stat-l">Conversions</div></div>
        </div>
      </div>

      <div className="cg-toolbar">
        <input placeholder="Search campaigns" value={search} onChange={e => setSearch(e.target.value)} />
        <div className="cg-divider" />
        <label className="cg-qa-filter" title="Show only campaigns whose DSP QA has been signed off">
          <input
            type="checkbox"
            checked={qaSignedOffOnly}
            onChange={e => setQaSignedOffOnly(e.target.checked)}
          />
          Only QA signed-off
        </label>
        <div className="cg-divider" />
        <label className="cg-time-filter" title="Show campaigns whose flight overlaps this window">
          <span className="cg-time-filter-l">Ran in</span>
          <select
            className="cg-time-select"
            aria-label="Time window"
            value={timeFilter.preset}
            onChange={e => {
              // 'custom' only REVEALS the date inputs — the window is still
              // the current one until the range is filled in. Every other
              // pick is a data operation (blank + auto-pull).
              if (e.target.value === 'custom') setTimeFilter(f => ({ ...f, preset: 'custom' }));
              else changeTimeWindow(e.target.value);
            }}
          >
            {GRID_TIME_PRESETS.map(p => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
          {timeFilter.preset === 'custom' && (
            <>
              <input
                type="date"
                className="cg-time-date"
                aria-label="Window start"
                value={timeFilter.startDate}
                onChange={e => changeCustomWindow({ startDate: e.target.value })}
              />
              <span className="cg-time-filter-l">→</span>
              <input
                type="date"
                className="cg-time-date"
                aria-label="Window end"
                value={timeFilter.endDate}
                onChange={e => changeCustomWindow({ endDate: e.target.value })}
              />
              <button
                type="button"
                className="cg-btn cg-btn-secondary cg-fetch-btn"
                onClick={fetchCustomWindow}
                disabled={!timeWindow?.startDate || !timeWindow?.endDate || refreshing || windowPending}
                title={refreshing || windowPending
                  ? 'Fetching metrics for this date range…'
                  : 'Fetch metrics for this date range — the reporting-API pull takes a while'}
              >
                Fetch
              </button>
            </>
          )}
        </label>
        {timeWindow && (
          <span
            className="cg-time-note"
            title={windowedRowCount === filteredCampaigns.length && filteredCampaigns.length > 0
              ? 'Metrics were refreshed for exactly this window.'
              : 'The rows are the campaigns that ran in this window. Rows without a matching refresh are re-pulled automatically when the window changes.'}
          >
            {describeTimeWindow(timeWindow)}
            {' · '}
            {windowedRowCount === filteredCampaigns.length && filteredCampaigns.length > 0
              ? 'windowed metrics'
              /* In-flight phrasing comes from ACTUAL pull state, not from the
                 stamps: a finished pull whose campaigns had zero delivery (or
                 platforms the pull doesn't cover) must not read "pulling this
                 window…" forever. */
              : (refreshing || windowPending
                ? (windowedRowCount > 0
                  ? `${windowedRowCount}/${filteredCampaigns.length} rows windowed — refresh in flight`
                  : 'pulling this window…')
                : (windowedRowCount > 0
                  ? `${windowedRowCount}/${filteredCampaigns.length} rows windowed`
                  : 'no data pulled for this window yet'))}
          </span>
        )}
        <div className="cg-divider" />
        <span style={{ fontSize: 11, color: 'var(--cg-text-3)' }}>
          {runs.filter(r => r.status === 'running' || r.status === 'queued').length} active run(s)
        </span>
      </div>

      <div className="cg-tabs">
        {TAB_DEFS.map(t => (
          <button
            key={t.id}
            className={`cg-tab ${activeTab === t.id ? 'active' : ''}`}
            onClick={() => selectTab(t)}
          >
            {t.label}
            <span className="cg-tab-count">{visibleColsOf(t).length}</span>
          </button>
        ))}
      </div>

      <div className="cg-grid-wrap">
        <div className="cg-grid-scroll">
          {loading && <div className="cg-loading">Loading campaigns…</div>}
          {error && <div className="cg-loading" style={{ color: 'var(--cg-red)' }}>{error}</div>}
          {!loading && filteredCampaigns.length === 0 && <div className="cg-empty">No campaigns match.</div>}

          {filteredCampaigns.length > 0 && (
            <table className="cg-table">
              <thead>
                <tr>
                  <th style={{ width: 22 }}></th>
                  <SortableTh
                    sortKey={NAME_SORT_KEY}
                    label="Campaign / Ad group"
                    style={{ width: 260 }}
                    sort={sort}
                    onSort={toggleSort}
                  />
                  {cols.map(c => (
                    <SortableTh
                      key={c.k}
                      sortKey={c.k}
                      label={c.l}
                      className={c.num ? 'cg-num' : ''}
                      sort={sort}
                      onSort={toggleSort}
                    />
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedCampaigns.map(c => {
                  const expanded = expandedIds.has(c.id);
                  // adGroupRowsOf prefers the lazily-fetched full tree (loaded on
                  // expand, with leaf assets) over the collapsed embed (ad groups
                  // + asset groups + ads, no assets) from GET /campaign-grid.
                  // Child rows inherit the campaign's currency_code (adgroup
                  // metric rows don't store one) so their money cells format
                  // consistently.
                  const ags = adGroupRowsOf(c, adgroupsByCampaign).map(
                    ag => (c.currency_code && !ag.currency_code ? { ...ag, currency_code: c.currency_code } : ag)
                  );

                  return (
                    <React.Fragment key={c.id}>
                      <tr
                        className={`cg-campaign-row ${expanded ? 'expanded' : ''}`}
                        onClick={() => openCampaignInClw(c)}
                        title={`Open ${c.name || 'this campaign'} in the launch workflow`}
                      >
                        <td onClick={e => { e.stopPropagation(); toggleExpand(c); }}>
                          <button
                            type="button"
                            className="cg-camp-chev"
                            title={expanded ? 'Collapse campaign' : 'Expand campaign'}
                            onClick={e => { e.stopPropagation(); toggleExpand(c); }}
                          >▸</button>
                        </td>
                        <td>
                          <div className="cg-camp-name-row">
                            <strong className="cg-camp-name-strong">{c.name}</strong>
                            {qaBadge(c)}
                            <button
                              type="button"
                              className="cg-camp-action"
                              title={`Open ${c.name || 'this campaign'} in the launch workflow`}
                              onClick={e => { e.stopPropagation(); openCampaignInClw(c); }}
                            >✎ Details</button>
                          </div>
                          <div className="cg-camp-meta">
                            {c.destination} · {c.folder_name || 'No folder'}
                            {ags.length > 0 && (
                              <> · <strong>{ags.length}</strong> ad {ags.length === 1 ? 'group' : 'groups'}</>
                            )}
                          </div>
                        </td>
                        {cols.map(col => (
                          <td key={col.k} className={col.num ? 'cg-num' : ''}>
                            {campaignFormats(c, adgroupsByCampaign).some(fmt => columnApplies(col, fmt))
                              ? campaignAggCell(c, col, ags)
                              : <span className="cg-dash" title="Not applicable to this campaign type">n/a</span>}
                          </td>
                        ))}
                      </tr>

                      {/* Level 2 — ad-group / asset-group rows (own perf). Each
                          with ads can expand to Level 3 — its ads. Row clicks
                          open the PARENT campaign in CLW, which is the closest
                          campaign-scoped surface now that the row panel is gone. */}
                      {expanded && ags.map((ag) => {
                        const agKey = `${c.id}:${ag.external_id}`;
                        const ads = ag.ads || [];
                        const agExpanded = expandedAdGroups.has(agKey);
                        // Children are ads under an ad group, assets (images /
                        // videos / headlines) under a PMax asset group, or line
                        // items under a DV360 insertion order. Named from the
                        // child's own kind: "3 ads" under an insertion order
                        // would be wrong in DV360's vocabulary, where the level
                        // below an IO is a line item and an ad is something else
                        // again.
                        const childLabel = CHILD_LABEL[ads.length ? ads[0].kind : ''] || 'ad';
                        return (
                          <React.Fragment key={agKey}>
                            <tr className="cg-tactic-row cg-pkg-start" onClick={() => openCampaignInClw(c)}>
                              <td className="cg-pkg-cell" />
                              <td className="cg-col-name cg-indent-pkg">
                                {ads.length > 0 && (
                                  <span
                                    className="cg-expand-btn"
                                    title={agExpanded ? 'Collapse' : 'Expand'}
                                    onClick={e => { e.stopPropagation(); toggleAdGroup(agKey); }}
                                  >{agExpanded ? '▾' : '▸'}</span>
                                )}
                                {ag.name}
                                {ads.length > 0 && <span className="cg-pill cg-pill-gray" style={{ marginLeft: 6 }}>{ads.length} {ads.length === 1 ? childLabel : `${childLabel}s`}</span>}
                                {ag.placeholder && <span className="cg-pill cg-pill-gray" style={{ marginLeft: 6 }}>placeholder</span>}
                              </td>
                              {cols.map(col => (
                                <td key={col.k} className={col.num ? 'cg-num' : ''}>{renderCell(col, ag)}</td>
                              ))}
                            </tr>

                            {/* Level 3 — ads (inherit ad-group format/budget, show
                                their own status / creative / perf). */}
                            {agExpanded && ads.map((ad) => {
                              const adRow = { ...ag, ...ad };
                              return (
                                <tr key={`${agKey}-${ad.external_id}`} className="cg-tactic-row cg-ad-row" onClick={() => openCampaignInClw(c)}>
                                  <td className="cg-pkg-cell" />
                                  <td className="cg-col-name" style={{ paddingLeft: 56 }}>
                                    ↳ {ad.name || ad.creative || ad.headline || `Ad ${ad.external_id}`}
                                  </td>
                                  {cols.map(col => (
                                    <td key={col.k} className={col.num ? 'cg-num' : ''}>{renderCell(col, adRow)}</td>
                                  ))}
                                </tr>
                              );
                            })}
                          </React.Fragment>
                        );
                      })}

                      {expanded && ags.length === 0 && (
                        <tr><td colSpan={colSpan} className="cg-loading">Loading ad groups…</td></tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

      </div>

      <div className="cg-foot">
        <span><strong>{filteredCampaigns.length}</strong> campaigns shown</span>
        <span>·</span>
        <span>{runs.length} run(s) this session</span>
      </div>

      {toast && <div className={`cg-toast ${toast.err ? 'err' : ''}`}>{toast.msg}</div>}
    </div>
  );
}

export default CampaignGridPage;
